import { useRef, useEffect, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowLeft, ArrowRight, CheckCircle, MapPin, Info, FileText, Loader2, AlertCircle } from "lucide-react";
import { cn } from "@/lib/utils";
import { format } from "date-fns";
import { apiRequest } from "@/lib/queryClient";
import { normalizePlanType, AgeBandType } from "@shared/schema";
import { CA_RATING_AREAS, findAreaByZip, mapWordBrownAreaLabelToId } from "@/lib/caRatingAreas";
import { parseWBBenefits } from "@/lib/wbBenefitsParser";
import type { Quote } from "@shared/schema";

import { useQuoteWizard } from "./useQuoteWizard";
import { QuoteWizardContext } from "./QuoteWizardContext";
import { QUOTE_STEPS, RFP_STEPS } from "./constants";
import type { WizardMode } from "../Quoting";
import type { QuoteContributionConfig } from "./types";
import {
  Step1SelectEntity,
  Step2CoverageTypes,
  Step3ReviewCensus,
  Step4SelectCarriers,
  Step4Recipients,
  Step5PlanSelection,
  Step6RFPParameters,
  Step7RFPPreview,
  Step8QuoteSummary,
  Step9GenerateQuote,
} from "./steps";
import { useSearchParams } from "wouter";
import { findGaSelectionProblems } from "@/lib/quoting/rfpGaSelection";

interface NewQuoteProcessProps {
  onClose: () => void;
  existingQuote?: Quote | null;
  mode?: WizardMode;
  initialStep?: number;
  initialClientId?: number | null;
  initialProspectId?: number | null;
  onStepChange?: (step: number) => void;
  onDraftIdChange?: (id: number | null) => void;
}

export function NewQuoteProcess({
  onClose,
  existingQuote,
  mode = "quote",
  initialStep = 1,
  initialClientId,
  initialProspectId,
  onStepChange,
  onDraftIdChange,
}: NewQuoteProcessProps) {
  const wizard = useQuoteWizard({
    existingQuote,
    mode,
    initialStep,
    initialClientId,
    initialProspectId,
    onStepChange,
    onDraftIdChange,
  });
  const isRFPMode = mode === "rfp";
  // Set by GET /api/quotes/:id — what the client was last sent, if anything.
  const sharedVersion = (existingQuote as any)?.sharedVersion as
    | { versionNumber: number; publishedAt: string | null; accepted: boolean; acceptedAt: string | null }
    | null
    | undefined;
  const STEPS = isRFPMode ? RFP_STEPS : QUOTE_STEPS;

  // Ref for scrolling content to top on step change
  const contentRef = useRef<HTMLDivElement>(null);

  const {
    currentStep,
    quoteData,
    saving,
    nextStep,
    prevStep,
    canProceed,
    carriers,
    rateDialogOpen,
    selectedPlanForRates,
    selectedAreaIndex,
    areaSearchTerm,
    isCreatingPlan,
    isFetchingRates,
    planAreaRates,
    setRateDialogOpen,
    setSelectedPlanForRates,
    setSelectedAreaIndex,
    setAreaSearchTerm,
    setIsCreatingPlan,
    setIsFetchingRates,
    setPlanAreaRates,
    updateQuoteData,
    extractMetalTier,
    normalizeNetworkType,
    toast,
  } = wizard;

  // State for detected rating area info (for dialog display)
  const [detectedRatingArea, setDetectedRatingArea] = useState<{
    label: string;
    areaNumber: number;
    counties: string[];
    zipCode: string;
  } | null>(null);

  const [isFinalizing, setIsFinalizing] = useState(false);
  const [finalizeConfirmOpen, setFinalizeConfirmOpen] = useState(false);

  // Finalize marks the quote as no longer a draft (status "sent"), so it locks
  // and drops out of the resumable list. "Save & Exit" leaves it a draft.
  // A quote with no draft id was never saved, so there is nothing to lock —
  // close without asking.
  const handleFinalizeClick = () => {
    if (!wizard.draftQuoteId) {
      onClose();
      return;
    }
    setFinalizeConfirmOpen(true);
  };

  const handleFinalize = async () => {
    if (!wizard.draftQuoteId) return;
    setFinalizeConfirmOpen(false);
    setIsFinalizing(true);
    try {
      // Status-only update — no wizardState, so it can't touch the saved plans.
      const res = await apiRequest("PUT", `/api/quotes/${wizard.draftQuoteId}`, {
        status: "sent",
      });
      if (!res.ok) throw new Error("Failed to finalize quote");
      toast({
        title: "Quote finalized",
        description: "The quote is locked and marked as finalized.",
      });
      onClose();
    } catch (error) {
      console.error("Failed to finalize quote:", error);
      toast({
        title: "Couldn't finalize",
        description: "Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsFinalizing(false);
    }
  };

  // Scroll content to top when step changes
  useEffect(() => {
    if (contentRef.current) {
      contentRef.current.scrollTop = 0;
    }
  }, [currentStep]);

  // The same call canProceed makes, turned into words. Only RFP step 4 blocks
  // for a reason that is not obvious from the step itself.
  const blockedReason = (() => {
    if (!isRFPMode || currentStep !== 4 || canProceed()) return null;
    const [problem] = findGaSelectionProblems({
      sendVia: quoteData.rfpSendVia,
      selectedGeneralAgencyIds: quoteData.rfpGeneralAgencyIds ?? [],
      asks: quoteData.rfpGeneralAgencyAsks ?? [],
    });
    if (!problem) return "Pick at least one carrier";
    if (problem.kind === "no-ga-picked") return "Tick at least one GA";
    const names = problem.generalAgencyIds
      .map((id) => wizard.generalAgencies.find((ga: any) => ga.id === id)?.name)
      .filter(Boolean);
    return `${names.join(", ") || "A GA"} has no carriers ticked`;
  })();

  // Fetch Word & Brown plan details with rates
  const fetchPlanDetailsWithRates = async (plan: any) => {
    setIsFetchingRates(true);
    try {
      const state = quoteData.selectedEntity?.state || "CA";
      const effectiveDate = quoteData.effectiveDate
        ? format(quoteData.effectiveDate, "yyyy-MM-dd")
        : format(new Date(), "yyyy-MM-dd");

      const carrier = carriers.find((c) => c.id === plan.localCarrierId);
      const carrierId = carrier?.wordBrownId;
      const planId = plan.wordBrownId || plan.id;
      const isMockPlan = plan.source === "mock";

      if (!planId) {
        console.error("Missing planId for fetching rates");
        return;
      }

      // Use different endpoint for mock plans vs Word & Brown plans
      let url: string;
      if (isMockPlan) {
        url = `/api/mock-plans/plan-details?planId=${planId}&state=${state}&effectiveDate=${effectiveDate}`;
      } else {
        if (!carrierId) {
          console.error("Missing carrierId for Word & Brown plan");
          return;
        }
        // Cutover: read rates from our DB (cache-first, live W&B fallback) instead
        // of calling W&B live. Same response shape as the W&B route.
        url = `/api/plan-source/plan-details?carrierId=${carrierId}&state=${state}&effectiveDate=${effectiveDate}&planId=${planId}&include=BenefitsAndRates`;
      }

      const res = await fetch(url);

      console.log(`[Rating Area] Plan-details response status: ${res.status}, ok: ${res.ok}, isMockPlan: ${isMockPlan}`);

      if (res.ok) {
        const json = await res.json();
        const data = json?.data;
        console.log(`[Rating Area] Plan data received, extracting areas...`);
        let areas = [] as any[];

        if (Array.isArray(data)) {
          const node = data[0];
          const planNode =
            Array.isArray(node?.Plans) && node.Plans.length
              ? node.Plans[0]
              : node;
          areas =
            planNode?.Areas ||
            planNode?.areaRateTable ||
            planNode?.AreaRateTable ||
            [];
        } else if (data) {
          const planNode =
            Array.isArray(data.Plans) && data.Plans.length
              ? data.Plans[0]
              : data;
          areas =
            planNode?.Areas ||
            planNode?.areaRateTable ||
            planNode?.AreaRateTable ||
            [];
        }

        console.log(`[Rating Area] Raw areas from API:`, areas?.length || 0, 'areas');

        const normalizedAreas = (Array.isArray(areas) ? areas : [])
          .map((a: any, idx: number) => {
            const label =
              a.AreaName ||
              a.areaName ||
              a.Name ||
              a.name ||
              a.RegionName ||
              a.regionName ||
              a.Region ||
              a.region ||
              a.AreaCode ||
              a.areaCode ||
              `Area ${idx + 1}`;
            const r = a.Rates || a.rates || [];
            return { label: String(label), rates: Array.isArray(r) ? r : [] };
          })
          .filter((x: any) => x.rates.length > 0);

        console.log(`[Rating Area] Normalized areas with rates:`, normalizedAreas.length);
        setPlanAreaRates(normalizedAreas);

        // Auto-select the matching area based on entity's ZIP code
        if (normalizedAreas.length > 0) {
          // Pass normalizedAreas so the returned index matches the dropdown options
          const { index: matchingIndex } = await findMatchingAreaIndex(normalizedAreas);
          setSelectedAreaIndex(matchingIndex);
          console.log(`[Rating Area] Auto-selected area index ${matchingIndex} for dialog`);
        } else {
          console.log(`[Rating Area] No areas with rates found, skipping area detection`);
        }
      } else {
        const fallbackAreas = parseAreaRates(plan);
        setPlanAreaRates(fallbackAreas);
        // Still try to detect rating area for display even in fallback
        if (fallbackAreas.length > 0) {
          const { index: matchingIndex } = await findMatchingAreaIndex(fallbackAreas);
          setSelectedAreaIndex(matchingIndex);
          console.log(`[Rating Area] Fallback: Auto-selected area index ${matchingIndex}`);
        }
      }
    } catch (error) {
      console.error("Error fetching plan details:", error);
      const fallbackAreas = parseAreaRates(plan);
      setPlanAreaRates(fallbackAreas);
      // Still try to detect rating area for display even on error
      if (fallbackAreas.length > 0) {
        const { index: matchingIndex } = await findMatchingAreaIndex(fallbackAreas);
        setSelectedAreaIndex(matchingIndex);
        console.log(`[Rating Area] Error fallback: Auto-selected area index ${matchingIndex}`);
      }
    } finally {
      setIsFetchingRates(false);
    }
  };

  // An area-agnostic rate table (manual plans without regions) is served as a
  // single synthetic "Standard" area (code "0"); it applies to every ZIP.
  const isAreaAgnostic = (area: any): boolean => {
    const label = String(area?.label || area?.AreaName || area?.areaName || area?.Name || area?.name || "");
    const code = String(area?.AreaCode ?? area?.areaCode ?? "");
    return code === "0" || /^standard$/i.test(label.trim());
  };

  // Find the matching area index based on confirmed rating area or entity's ZIP
  // code (async - uses API). `matched` is false when the entity's area is known
  // but the plan has no rates for it — callers that price a plan must treat
  // that as "no rate", not silently use the first (wrong) area.
  const findMatchingAreaIndex = async (
    areas: any[],
    updateDetectedArea = true,
  ): Promise<{ index: number; label: string; matched: boolean }> => {
    const entityZip = quoteData.selectedEntity?.zipCode;
    const confirmedArea = quoteData.confirmedRatingArea;
    console.log(`[Rating Area] findMatchingAreaIndex called - confirmedArea: ${confirmedArea?.label}, entityZip: ${entityZip}, areas count: ${areas?.length}, updateDetectedArea: ${updateDetectedArea}`);

    if (!areas || areas.length === 0) {
      // Default to first area
      console.log(`[Rating Area] No areas - returning default`);
      const firstArea = areas?.[0];
      const label = firstArea?.AreaName || firstArea?.areaName || firstArea?.Name || firstArea?.name || "Area 1";
      if (updateDetectedArea) setDetectedRatingArea(null);
      return { index: 0, label: String(label), matched: false };
    }

    // Area-agnostic rate table: the single "Standard" area applies everywhere.
    if (areas.length === 1 && isAreaAgnostic(areas[0])) {
      const a = areas[0];
      const label = a?.label || a?.AreaName || a?.areaName || a?.Name || a?.name || "Standard";
      return { index: 0, label: String(label), matched: true };
    }

    // PRIORITY 1: Use confirmed rating area from Step 1 if available
    let entityRatingAreaLabel: string | null = null;
    let ratingAreaData: any = null;
    
    if (confirmedArea?.label) {
      entityRatingAreaLabel = confirmedArea.label;
      ratingAreaData = {
        label: confirmedArea.label,
        areaNumber: confirmedArea.id,
      };
      console.log(`[Rating Area] Using confirmed area from Step 1: ${entityRatingAreaLabel}`);
    }
    
    // PRIORITY 2: Fall back to API lookup if no confirmed area. Pass the
    // entity's state so the geo resolver scopes correctly (area numbers repeat
    // across states); the lookup also returns the area's counties inline.
    const entityState = quoteData.selectedEntity?.state;
    const entityCounty = quoteData.selectedEntity?.county;
    if (!entityRatingAreaLabel && entityZip) {
      try {
        const res = await fetch(
          `/api/rating-areas/lookup?zipCode=${entityZip}${entityState ? `&state=${encodeURIComponent(entityState)}` : ""}${entityCounty ? `&county=${encodeURIComponent(entityCounty)}` : ""}`,
        );
        if (res.ok) {
          ratingAreaData = await res.json();
          entityRatingAreaLabel = ratingAreaData?.label; // e.g., "AREA 4"
          console.log(`[Rating Area] API lookup for ZIP ${entityZip} returned: ${entityRatingAreaLabel}`);
        }
      } catch (error) {
        console.error(`[Rating Area] API lookup failed for ZIP ${entityZip}:`, error);
      }
    }

    // PRIORITY 3: Fall back to client-side lookup if API fails
    if (!entityRatingAreaLabel && entityZip) {
      const entityRatingArea = findAreaByZip(entityZip);
      if (entityRatingArea) {
        entityRatingAreaLabel = entityRatingArea.label;
        // Use client-side data for counties
        ratingAreaData = {
          label: entityRatingArea.label,
          areaNumber: entityRatingArea.id,
        };
        console.log(`[Rating Area] Client-side fallback for ZIP ${entityZip}: ${entityRatingAreaLabel}`);
      }
    }

    if (!entityRatingAreaLabel) {
      console.log(`[Rating Area] No rating area found, using first area`);
      const firstArea = areas[0];
      const label = firstArea?.AreaName || firstArea?.areaName || firstArea?.Name || firstArea?.name || "Area 1";
      if (updateDetectedArea) setDetectedRatingArea(null);
      return { index: 0, label: String(label), matched: false };
    }

    // Set the detected area for display. The lookup already returns the area's
    // counties inline (no second round-trip); fall back to bundled CA data.
    if (updateDetectedArea && Array.isArray(ratingAreaData?.counties)) {
      const counties = ratingAreaData.counties.map((c: any) =>
        typeof c === "string" ? c : c.county,
      );
      console.log(`[Rating Area] Setting detectedRatingArea from API:`, { label: ratingAreaData.label, areaNumber: ratingAreaData.areaNumber, counties, zipCode: entityZip });
      setDetectedRatingArea({
        label: ratingAreaData.label,
        areaNumber: ratingAreaData.areaNumber,
        counties,
        zipCode: entityZip,
      });
    } else if (updateDetectedArea) {
      // Use client-side data for counties
      const areaId = mapWordBrownAreaLabelToId(entityRatingAreaLabel);
      const clientArea = CA_RATING_AREAS.find(a => a.id === areaId);
      setDetectedRatingArea({
        label: entityRatingAreaLabel,
        areaNumber: areaId || 0,
        counties: clientArea?.counties || [],
        zipCode: entityZip,
      });
    }

    // Find matching area in the plan's areas
    const targetAreaId = mapWordBrownAreaLabelToId(entityRatingAreaLabel);
    console.log(`[Rating Area] Entity ZIP ${entityZip} maps to ${entityRatingAreaLabel} (ID: ${targetAreaId})`);
    console.log(`[Rating Area] Searching through ${areas.length} areas for match...`);

    for (let i = 0; i < areas.length; i++) {
      const area = areas[i];
      // Support both raw Word & Brown format (AreaName) and normalized format (label)
      const areaLabel = area?.label || area?.AreaName || area?.areaName || area?.Name || area?.name || `Area ${i + 1}`;
      const areaId = mapWordBrownAreaLabelToId(String(areaLabel));
      console.log(`[Rating Area]   Area ${i}: "${areaLabel}" -> ID ${areaId}`);

      if (areaId === targetAreaId) {
        console.log(`[Rating Area] Found matching area at index ${i}: ${areaLabel}`);
        return { index: i, label: String(areaLabel), matched: true };
      }
    }

    // No match found. Report it (matched: false) and point at the first area
    // only for UI paths (the rate dialog) where the user picks explicitly.
    console.log(`[Rating Area] No matching area found for ${entityRatingAreaLabel} in plan areas`);
    const firstArea = areas[0];
    const label = firstArea?.label || firstArea?.AreaName || firstArea?.areaName || firstArea?.Name || firstArea?.name || "Area 1";
    return { index: 0, label: String(label), matched: false };
  };

  // Fetch rates for bulk import (returns matching area's rates based on entity ZIP)
  const fetchPlanRatesForBulkImport = async (plan: any): Promise<{ areaLabel: string; rates: any[]; benefits?: any[] } | null> => {
    try {
      const state = quoteData.selectedEntity?.state || "CA";
      const effectiveDate = quoteData.effectiveDate
        ? format(quoteData.effectiveDate, "yyyy-MM-dd")
        : format(new Date(), "yyyy-MM-dd");

      const carrier = carriers.find((c) => c.id === plan.localCarrierId);
      // Provider carrier id when known, else our internal id (manual carriers).
      const carrierId = carrier?.wordBrownId ?? (carrier ? String(carrier.id) : undefined);
      // Prefer our internal library plan id (planId) — provider ids are only
      // set for W&B-synced plans; the plan-source endpoint resolves both.
      const planId = plan.wordBrownId || plan.planId || plan.id;
      const isMockPlan = plan.source === "mock";

      console.log("[Bulk Import] Fetching rates for plan:", plan.name, { carrierId, planId, state, effectiveDate, isMockPlan });

      if (!planId) {
        console.error("[Bulk Import] Missing planId for fetching rates");
        return null;
      }

      // Use different endpoint for mock plans vs library plans
      let url: string;
      if (isMockPlan) {
        url = `/api/mock-plans/plan-details?planId=${planId}&state=${state}&effectiveDate=${effectiveDate}`;
      } else {
        url = `/api/plan-source/plan-details?${carrierId ? `carrierId=${carrierId}&` : ""}state=${state}&effectiveDate=${effectiveDate}&planId=${planId}&include=BenefitsAndRates`;
      }
      console.log("[Bulk Import] Fetching from URL:", url);

      const res = await fetch(url);

      if (!res.ok) {
        console.error("[Bulk Import] Failed to fetch plan details:", res.status);
        return null;
      }

      const json = await res.json();
      console.log("[Bulk Import] Raw API response for", plan.name, json);

      const data = json?.data;
      let areas: any[] = [];
      let benefits: any[] = [];

      // Flatten W&B Benefits object ({ BenefitSet1: [...], BenefitSet2: [...] }) into a single array
      const flattenBenefits = (raw: any): any[] => {
        if (Array.isArray(raw)) return raw;
        if (raw && typeof raw === "object") {
          const allSets = Object.values(raw).filter(Array.isArray) as any[][];
          return allSets.flat();
        }
        return [];
      };

      if (Array.isArray(data)) {
        const node = data[0];
        const planNode =
          Array.isArray(node?.Plans) && node.Plans.length
            ? node.Plans[0]
            : node;
        areas =
          planNode?.Areas ||
          planNode?.areaRateTable ||
          planNode?.AreaRateTable ||
          [];
        // Extract benefits from the plan node (W&B returns { BenefitSet1: [...], ... })
        benefits = flattenBenefits(planNode?.Benefits || planNode?.benefits);
      } else if (data) {
        const planNode =
          Array.isArray(data.Plans) && data.Plans.length
            ? data.Plans[0]
            : data;
        areas =
          planNode?.Areas ||
          planNode?.areaRateTable ||
          planNode?.AreaRateTable ||
          [];
        // Extract benefits from the plan node (W&B returns { BenefitSet1: [...], ... })
        benefits = flattenBenefits(planNode?.Benefits || planNode?.benefits);
      }

      console.log("[Bulk Import] Extracted areas for", plan.name, ":", areas?.length || 0, "areas,", benefits?.length || 0, "benefits");

      if (!Array.isArray(areas) || areas.length === 0) {
        console.log("[Bulk Import] No areas found for plan:", plan.name);
        return null;
      }

      // Find the matching area based on entity's ZIP code (don't update dialog state for bulk imports)
      const { index: matchingIndex, label: areaLabel, matched } = await findMatchingAreaIndex(areas, false);
      if (!matched && (quoteData.confirmedRatingArea?.label || quoteData.selectedEntity?.zipCode)) {
        // The plan has rates, but not for this entity's rating area. Never price
        // (or cache) it off another area's table.
        console.log("[Bulk Import] No rates for the entity's rating area for plan:", plan.name);
        return null;
      }
      const matchingArea = areas[matchingIndex];
      const rates = matchingArea?.Rates || matchingArea?.rates || [];

      console.log("[Bulk Import] Using area for", plan.name, ":", areaLabel, "(index:", matchingIndex, ") with", rates.length, "rates");

      if (rates.length === 0) {
        console.log("[Bulk Import] No rates in matching area for plan:", plan.name);
        return null;
      }

      return { areaLabel: String(areaLabel), rates, benefits };
    } catch (error) {
      console.error("[Bulk Import] Error fetching plan rates:", error);
      return null;
    }
  };

  const parseAreaRates = (plan: any) => {
    const areas =
      plan.Areas ||
      plan.areaRateTable ||
      plan.AreaRateTable ||
      plan.rates ||
      plan.Rates ||
      [];
    let normalized: { label: string; rates: any[] }[] = [];

    if (Array.isArray(areas)) {
      normalized = areas
        .map((a: any, idx: number) => {
          const label =
            a?.AreaName ||
            a?.areaName ||
            a?.Name ||
            a?.name ||
            a?.label ||
            `Area ${idx + 1}`;
          const r = a?.Rates || a?.rates || [];
          return { label: String(label), rates: Array.isArray(r) ? r : [] };
        })
        .filter((x) => x.rates.length > 0);
    }

    if (normalized.length === 0) {
      const r = Array.isArray(areas) ? areas : [];
      normalized = [{ label: "Rates", rates: r }];
    }

    return normalized;
  };

  const searchRatingArea = (searchTerm: string): number | undefined => {
    const term = searchTerm.trim();
    if (!term) return undefined;

    if (/^\d{5}$/.test(term)) {
      return findAreaByZip(term)?.id;
    }

    if (/^\d{3}$/.test(term)) {
      const match = CA_RATING_AREAS.find(
        (a) => Array.isArray(a.zipPrefixes) && a.zipPrefixes.includes(term)
      );
      return match?.id;
    }

    const mapped =
      mapWordBrownAreaLabelToId(term) ||
      mapWordBrownAreaLabelToId(`Area ${term}`);
    if (mapped) return mapped;

    const countyMatch = CA_RATING_AREAS.find((a) =>
      a.counties.some((c) => c.toLowerCase().includes(term.toLowerCase()))
    );
    return countyMatch?.id;
  };

  // Create plan from Word & Brown
  const createPlanFromWordBrown = async (
    wbPlan: any,
    selectedRates?: { areaIndex: number; rates: any[]; areaLabel?: string; benefits?: any[] }
  ): Promise<number | null> => {
    try {
      const metalTier = extractMetalTier(wbPlan);
      const networkType = normalizeNetworkType(wbPlan.networkType);

      // For non-health plans (life, disability), networkType may be N/A - default to PPO
      const validNetworkTypes = ["HMO", "PPO", "EPO", "POS", "HDHP"];
      const finalNetworkType = networkType && validNetworkTypes.includes(networkType) ? networkType : "PPO";

      const res = await apiRequest("POST", "/api/plans", {
        name: wbPlan.name,
        type: normalizePlanType(wbPlan.type),
        carrierId: wbPlan.localCarrierId,
        clientId: null,
        planCode: wbPlan.planCode || `WB-${wbPlan.wordBrownId}`,
        networkType: finalNetworkType,
        metalTier: metalTier || "Silver",
        description: `Imported plan (ref ${wbPlan.wordBrownId})`,
        commissionRate: wbPlan.commissionRate || "0.00",
        isActive: true,
        effectiveDate: wbPlan.effectiveDate || quoteData.effectiveDate,
      });
      const response: any = await res.json();
      const newPlanId = response?.id;

      if (!newPlanId) {
        throw new Error("Failed to get plan ID from response");
      }

      if (
        selectedRates &&
        selectedRates.rates &&
        selectedRates.rates.length > 0
      ) {
        // Use provided areaLabel or fall back to planAreaRates state
        const selectedArea = planAreaRates[selectedRates.areaIndex];
        const areaLabel = selectedRates.areaLabel || selectedArea?.label;
        const ratingAreaId = areaLabel
          ? mapWordBrownAreaLabelToId(areaLabel)
          : undefined;

        const ageBandRates: Record<string, string> = {};
        for (const rate of selectedRates.rates) {
          const ageFrom = (
            rate.AgeFrom ??
            rate.ageFrom ??
            rate.Age ??
            rate.age
          )?.toString();
          const premium = (
            rate.Premium ??
            rate.premium ??
            rate.Rate ??
            rate.rate
          )?.toString();
          if (ageFrom && premium) {
            ageBandRates[ageFrom] = premium;
          }
        }

        await apiRequest("PUT", `/api/plan-age-rates/bulk/${newPlanId}`, {
          clientId: null,
          ageBandType: AgeBandType.ALL_AGES,
          ratingMethod: "age_table",
          ageBandRates,
          ratingAreaId,
          ratingAreaLabel: areaLabel,
          origin: "wb_api",
        });
      }

      // Save structured benefits from W&B data
      const rawBenefits = selectedRates?.benefits || wbPlan.Benefits || wbPlan.benefits || [];
      const hasBenefits = Array.isArray(rawBenefits) ? rawBenefits.length > 0 : (rawBenefits && typeof rawBenefits === "object" && Object.keys(rawBenefits).length > 0);
      if (hasBenefits && newPlanId) {
        try {
          const parsedBenefits = parseWBBenefits(rawBenefits);
          if (Object.keys(parsedBenefits).length > 0) {
            await apiRequest("POST", "/api/plan-benefits/bulk", {
              planId: newPlanId,
              benefits: parsedBenefits,
            });
          }
        } catch (err) {
          console.error("Failed to save W&B benefits:", err);
          // Non-fatal: plan creation succeeds even if benefits save fails
        }
      }

      return newPlanId;
    } catch (error) {
      console.error("Error creating plan from Word & Brown:", error);
      toast({
        title: "Plan Creation Error",
        description: "Failed to save plan to database",
        variant: "destructive",
      });
      return null;
    }
  };

  // Download preview PDF with report type
  const downloadPreviewPDF = async (reportType?: string, contributionConfig?: QuoteContributionConfig) => {
    try {
      // Extract unique employees and dependents from plan breakdowns
      const employeeMap = new Map<number, any>();
      const dependentMap = new Map<number, any>();
      
      (wizard.premiumSummary.planBreakdowns || []).forEach((breakdown: any) => {
        (breakdown.enrollments || []).forEach((enrollment: any) => {
          if (enrollment.employee) {
            employeeMap.set(enrollment.employee.id, enrollment.employee);
          }
          if (enrollment.dependents) {
            enrollment.dependents.forEach((dep: any) => {
              if (dep) dependentMap.set(dep.id, dep);
            });
          }
        });
      });
      
      const employees = Array.from(employeeMap.values());
      const dependents = Array.from(dependentMap.values());
      
      // Use estimated values (one plan per coverage type) for totals, not sum of ALL options
      const response = await fetch("/api/quotes/preview-pdf", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          companyName: quoteData.selectedEntity?.name || "Company",
          effectiveDate: quoteData.effectiveDate,
          employeeCount: quoteData.selectedEmployees.length,
          coverageTypes: quoteData.coverageTypes,
          selectedPlans: quoteData.selectedPlans,
          currentPlans: quoteData.currentPlans,
          reportType: reportType || "standard",
          employees,
          dependents,
          premiumSummary: {
            totalMonthlyPremium: wizard.premiumSummary.estimatedMonthlyPremium || 0,
            totalAnnualPremium: wizard.premiumSummary.estimatedAnnualPremium || 0,
            totalEmployeePortion:
              wizard.premiumSummary.estimatedEmployeePortion || 0,
            totalEmployerPortion:
              wizard.premiumSummary.estimatedEmployerPortion || 0,
            planBreakdowns: wizard.premiumSummary.planBreakdowns || [],
            byCoverageType: wizard.premiumSummary.byCoverageType || {},
            premiumRangeMin: wizard.premiumSummary.premiumRangeMin || 0,
            premiumRangeMax: wizard.premiumSummary.premiumRangeMax || 0,
          },
          // One plan per coverage type, so "estimated" and "total" are the
          // same number here — still sent in full so the server's shape
          // matches premiumSummary exactly and collectPlans() can look up
          // each current plan's breakdown the same way it looks up renewal's.
          currentPremiumSummary: {
            totalMonthlyPremium:
              wizard.currentPremiumSummary.estimatedMonthlyPremium || 0,
            totalAnnualPremium:
              wizard.currentPremiumSummary.estimatedAnnualPremium || 0,
            totalEmployeePortion:
              wizard.currentPremiumSummary.estimatedEmployeePortion || 0,
            totalEmployerPortion:
              wizard.currentPremiumSummary.estimatedEmployerPortion || 0,
            planBreakdowns: wizard.currentPremiumSummary.planBreakdowns || [],
            byCoverageType: wizard.currentPremiumSummary.byCoverageType || {},
            premiumRangeMin: wizard.currentPremiumSummary.premiumRangeMin || 0,
            premiumRangeMax: wizard.currentPremiumSummary.premiumRangeMax || 0,
          },
          contributionConfig: contributionConfig || undefined,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || "Failed to generate PDF");
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const reportLabel = reportType ? reportType.replace(/_/g, "-") : "proposal";
      a.download = `${reportLabel}-${
        quoteData.selectedEntity?.name?.replace(/[^a-z0-9]/gi, "-") || "company"
      }-${new Date().toISOString().split("T")[0]}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      toast({
        title: "Success",
        description: "Report PDF downloaded successfully",
      });
    } catch (error: any) {
      console.error("Error downloading PDF:", error);
      toast({
        title: "Error",
        description: error?.message || "Failed to download report PDF",
        variant: "destructive",
      });
    }
  };

  // Render rate selection dialog
  const renderRateSelectionDialog = () => {
    if (!selectedPlanForRates) return null;

    const areaRates = planAreaRates;
    const currentArea = areaRates[selectedAreaIndex];
    const displayRates = currentArea?.rates || [];
    const areaSearchResolvedId = searchRatingArea(areaSearchTerm);

    const displayMetalTier = extractMetalTier(selectedPlanForRates) || "N/A";
    const displayNetworkType =
      normalizeNetworkType(selectedPlanForRates.networkType) ||
      selectedPlanForRates.networkType ||
      "N/A";

    const visibleAreaIndexes = areaSearchResolvedId
      ? areaRates
          .map((a, i) => ({ area: a, index: i }))
          .filter(({ area }) => {
            const areaId = mapWordBrownAreaLabelToId(area.label);
            return areaId === areaSearchResolvedId;
          })
          .map(({ index }) => index)
      : areaRates.map((_, i) => i);

    return (
      <Dialog open={rateDialogOpen} onOpenChange={setRateDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Select Rating Area & Rates</DialogTitle>
            <p className="text-sm text-muted-foreground">
              Choose the rating area for this plan to import rates
            </p>
          </DialogHeader>

          <div className="grid grid-cols-2 gap-3 text-sm border-b pb-4">
            <div>
              <div className="text-muted-foreground">Plan Name</div>
              <div className="font-medium">{selectedPlanForRates.name}</div>
            </div>
            <div>
              <div className="text-muted-foreground">Carrier</div>
              <div className="font-medium">
                {selectedPlanForRates.carrierName}
              </div>
            </div>
            <div>
              <div className="text-muted-foreground">Network Type</div>
              <div className="font-medium">{displayNetworkType}</div>
            </div>
            <div>
              <div className="text-muted-foreground">Metal Tier</div>
              <div className="font-medium">{displayMetalTier}</div>
            </div>
          </div>

          {areaRates.length > 0 ? (
            <>
              {/* Detected Rating Area Info */}
              {detectedRatingArea && (
                <Alert className="bg-blue-50 border-blue-200">
                  <MapPin className="h-4 w-4 text-blue-600" />
                  <AlertDescription className="text-sm">
                    <div className="flex items-start justify-between">
                      <div>
                        <span className="font-medium text-blue-900">
                          {detectedRatingArea.label}
                        </span>
                        <span className="text-blue-700 ml-1">
                          detected from ZIP {detectedRatingArea.zipCode}
                        </span>
                        {detectedRatingArea.counties.length > 0 && (
                          <div className="text-blue-600 text-xs mt-1">
                            Counties: {detectedRatingArea.counties.slice(0, 5).join(", ")}
                            {detectedRatingArea.counties.length > 5 && ` +${detectedRatingArea.counties.length - 5} more`}
                          </div>
                        )}
                      </div>
                      <Badge variant="outline" className="bg-blue-100 text-blue-800 border-blue-300 text-xs">
                        Auto-selected
                      </Badge>
                    </div>
                  </AlertDescription>
                </Alert>
              )}

              <div className="space-y-2">
                <label className="text-sm font-medium">Rating Area</label>
                <div className="flex gap-2">
                  <Select
                    value={String(selectedAreaIndex)}
                    onValueChange={(v) => setSelectedAreaIndex(parseInt(v, 10))}
                  >
                    <SelectTrigger className="w-64">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {areaRates
                        .map((area, originalIndex) => ({ area, originalIndex }))
                        .filter(({ originalIndex }) => visibleAreaIndexes.includes(originalIndex))
                        .map(({ area, originalIndex }) => (
                          <SelectItem key={originalIndex} value={String(originalIndex)}>
                            {area.label}
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>

                  <Input
                    placeholder="Search by county, ZIP, or Area #"
                    value={areaSearchTerm}
                    onChange={(e) => setAreaSearchTerm(e.target.value)}
                    className="flex-1"
                  />
                </div>

                {areaSearchTerm && (
                  <Badge
                    variant={areaSearchResolvedId ? "default" : "secondary"}
                    className="text-xs"
                  >
                    {areaSearchResolvedId
                      ? `Area ${areaSearchResolvedId} found`
                      : "No match"}
                  </Badge>
                )}
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium">Age-Based Rates</label>
                {isFetchingRates ? (
                  <div className="flex items-center justify-center p-8 text-sm text-muted-foreground">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary mr-2"></div>
                    Loading rates...
                  </div>
                ) : displayRates.length === 0 ? (
                  <div className="text-sm text-muted-foreground text-center p-4 border rounded">
                    No rates available for this plan
                  </div>
                ) : (
                  <>
                    <div className="max-h-64 overflow-y-auto border rounded">
                      <table className="w-full text-sm">
                        <thead className="bg-muted/50 sticky top-0">
                          <tr>
                            <th className="text-left p-2 font-medium">Age</th>
                            <th className="text-right p-2 font-medium">
                              Premium ($/mo)
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {displayRates.map((rate: any, i: number) => {
                            const ageFrom =
                              rate.AgeFrom ??
                              rate.ageFrom ??
                              rate.Age ??
                              rate.age;
                            const ageTo = rate.AgeTo ?? rate.ageTo;
                            const premium =
                              rate.Premium ??
                              rate.premium ??
                              rate.Rate ??
                              rate.rate;

                            return (
                              <tr key={i} className="border-t">
                                <td className="p-2">
                                  {ageFrom}
                                  {ageTo && ageTo !== ageFrom
                                    ? ` - ${ageTo}`
                                    : "+"}
                                </td>
                                <td className="p-2 text-right font-medium">
                                  ${premium}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {displayRates.length} rate tiers available
                    </p>
                  </>
                )}
              </div>

              <div className="flex justify-end gap-2 pt-4 border-t">
                <Button
                  variant="outline"
                  onClick={() => {
                    setRateDialogOpen(false);
                    setSelectedPlanForRates(null);
                    setSelectedAreaIndex(0);
                    setAreaSearchTerm("");
                    setDetectedRatingArea(null);
                  }}
                >
                  Cancel
                </Button>
                <Button
                  disabled={
                    isFetchingRates ||
                    isCreatingPlan ||
                    displayRates.length === 0
                  }
                  onClick={async () => {
                    setIsCreatingPlan(true);
                    const planId = await createPlanFromWordBrown(
                      selectedPlanForRates,
                      {
                        areaIndex: selectedAreaIndex,
                        rates: displayRates,
                      }
                    );

                    if (planId) {
                      const planToAdd = { ...selectedPlanForRates, id: planId };
                      const coverageType = selectedPlanForRates.type;
                      const currentPlans =
                        quoteData.selectedPlans[coverageType] || [];

                      updateQuoteData({
                        selectedPlans: {
                          ...quoteData.selectedPlans,
                          [coverageType]: [...currentPlans, planToAdd],
                        },
                      });

                      toast({
                        title: "Plan Added",
                        description: `${selectedPlanForRates.name} has been added with rates from ${currentArea?.label}`,
                      });

                      setRateDialogOpen(false);
                      setSelectedPlanForRates(null);
                      setSelectedAreaIndex(0);
                      setAreaSearchTerm("");
                      setDetectedRatingArea(null);
                    }
                    setIsCreatingPlan(false);
                  }}
                >
                  {isCreatingPlan ? "Adding Plan..." : "Add Plan with Rates"}
                </Button>
              </div>
            </>
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              <p>No rating areas available for this plan</p>
              <p className="text-xs mt-2">
                This plan may not have rate information from Word & Brown
              </p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    );
  };

  // Render current step - different flow for Quote vs RFP mode
  const renderCurrentStep = () => {
    if (isRFPMode) {
      // RFP Mode: 1=Entity, 2=Coverage, 3=Census, 4=Carriers, 5=RFP Params, 6=RFP Preview
      switch (currentStep) {
        case 1:
          return <Step1SelectEntity />;
        case 2:
          return <Step2CoverageTypes />;
        case 3:
          return <Step3ReviewCensus />;
        case 4:
          // RFP only: GAs first, then carriers inside each one. Quote mode keeps
          // Step4SelectCarriers below, which has no GA concept.
          return <Step4Recipients />;
        case 5:
          return <Step6RFPParameters />;
        case 6:
          return <Step7RFPPreview onComplete={onClose} />;
        default:
          return null;
      }
    }

    // Quote Mode: 7-step flow (no RFP steps)
    // 1=Entity, 2=Coverage, 3=Census, 4=Carriers, 5=Plan Selection, 6=Summary, 7=Generate
    switch (currentStep) {
      case 1:
        return <Step1SelectEntity />;
      case 2:
        return <Step2CoverageTypes />;
      case 3:
        return <Step3ReviewCensus />;
      case 4:
        return <Step4SelectCarriers />;
      case 5:
        return (
          <Step5PlanSelection
            fetchPlanDetailsWithRates={fetchPlanDetailsWithRates}
            createPlanFromWordBrown={createPlanFromWordBrown}
            fetchPlanRatesForBulkImport={fetchPlanRatesForBulkImport}
          />
        );
      case 6:
        return <Step8QuoteSummary />;
      case 7:
        return (
          <Step9GenerateQuote downloadPreviewPDF={downloadPreviewPDF} />
        );
      default:
        return null;
    }
  };

  return (
    <QuoteWizardContext.Provider value={wizard}>
      <div className="space-y-4">
        <div className="h-[calc(100vh-100px)] flex flex-col">
          <div className="flex flex-col h-full space-y-4">
            {/* Back Button + Title */}
            <div className="flex items-end justify-between">
              <div>
                <Button variant="ghost" onClick={onClose} className="-ml-2 mb-1">
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  Back to Quotes
                </Button>
                <h1 className="text-xl font-bold">
                  {isRFPMode ? "New RFP" : "New Quote"}
                </h1>
                <p className="text-sm text-muted-foreground">
                  {isRFPMode
                    ? "Request proposals from carriers"
                    : "Build a priced quote with selected plans"}
                </p>
              </div>
              <Badge
                variant={isRFPMode ? "secondary" : "default"}
                className="text-sm px-3 py-1"
              >
                {isRFPMode ? "RFP Mode" : "Quote Mode"}
              </Badge>
            </div>

            {/* A quote shared with a client is frozen as a version, so editing here
                changes a working draft rather than what they were sent. Say so,
                otherwise a broker reasonably assumes an edit reaches the client. */}
            {sharedVersion && (
              <div
                className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-xs ${
                  sharedVersion.accepted
                    ? "border-emerald-300 bg-emerald-50 dark:border-emerald-800 dark:bg-emerald-950/30"
                    : "border-blue-300 bg-blue-50 dark:border-blue-800 dark:bg-blue-950/30"
                }`}
              >
                {sharedVersion.accepted ? (
                  <CheckCircle className="h-4 w-4 text-emerald-600 dark:text-emerald-500 shrink-0" />
                ) : (
                  <Info className="h-4 w-4 text-blue-600 dark:text-blue-400 shrink-0" />
                )}
                <span>
                  {sharedVersion.accepted ? (
                    <>
                      The client <strong>accepted version {sharedVersion.versionNumber}</strong>.
                      Anything you change here becomes a new version. What they signed stays as it
                      is until you share again.
                    </>
                  ) : (
                    <>
                      <strong>Version {sharedVersion.versionNumber}</strong> was shared with the
                      client. Changes here won't reach them until you share again.
                    </>
                  )}
                </span>
              </div>
            )}

            {/* Progress Steps */}
            <Card className="flex-shrink-0">
              <CardContent className="p-4">
                <div className="flex items-center justify-between overflow-x-auto">
                  {STEPS.map((step, index) => {
                    // A step is completed if it's <= highestCompletedStep (not just < currentStep)
                    const isCompleted = step.id <= wizard.highestCompletedStep;
                    const isCurrent = currentStep === step.id;
                    const canNavigate =
                      wizard.isStepAccessible(step.id) && !isCurrent;

                    return (
                      <div key={step.id} className="flex items-center min-w-0">
                        <div
                          className={cn(
                            "flex items-center justify-center w-6 h-6 rounded-full text-xs font-medium flex-shrink-0 transition-all",
                            isCurrent
                              ? "bg-primary text-primary-foreground"
                              : isCompleted
                              ? "bg-green-500 text-white cursor-pointer hover:bg-green-600 hover:scale-110"
                              : canNavigate
                              ? "bg-muted text-muted-foreground cursor-pointer hover:bg-muted/80 hover:scale-105"
                              : "bg-muted text-muted-foreground"
                          )}
                          onClick={() => {
                            if (canNavigate) {
                              wizard.goToStep(step.id);
                            }
                          }}
                          title={
                            canNavigate ? `Go to ${step.title}` : undefined
                          }
                        >
                          {isCompleted ? (
                            <CheckCircle className="h-3 w-3" />
                          ) : (
                            step.id
                          )}
                        </div>
                        <div
                          className={cn(
                            "ml-2 hidden lg:block min-w-0",
                            canNavigate && "cursor-pointer hover:text-primary"
                          )}
                          onClick={() => {
                            if (canNavigate) {
                              wizard.goToStep(step.id);
                            }
                          }}
                        >
                          <div className="text-xs font-medium truncate">
                            {step.title}
                          </div>
                          <div className="text-xs text-muted-foreground truncate">
                            {step.description}
                          </div>
                        </div>
                        {index < STEPS.length - 1 && (
                          <div
                            className={cn(
                              "w-8 h-0.5 mx-2 flex-shrink-0",
                              isCompleted ? "bg-green-500" : "bg-muted"
                            )}
                          />
                        )}
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>

            {/* Current Step Content */}
            <Card className="flex-1 overflow-hidden min-h-0">
              <CardContent
                ref={contentRef}
                className="p-6 h-full overflow-y-auto"
              >
                {renderCurrentStep()}
              </CardContent>
            </Card>

            {/* Navigation - pushed to bottom */}
            <div className="flex justify-between flex-shrink-0 mt-auto pt-2">
              <Button
                variant="outline"
                onClick={prevStep}
                disabled={currentStep === 1 || saving}
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Previous
              </Button>

              <div className="flex items-center space-x-3">
                {saving && (
                  <div className="flex items-center text-sm text-muted-foreground">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary mr-2"></div>
                    Saving draft...
                  </div>
                )}
                {/* Why Next is disabled. The button has no tooltip, so without
                    this the broker gets a dead control and no reason. */}
                {!saving && blockedReason && (
                  <div className="flex items-center gap-1.5 text-xs text-amber-700 dark:text-amber-500">
                    <AlertCircle className="h-3.5 w-3.5 shrink-0" />
                    {blockedReason}
                  </div>
                )}
                {/* A step can portal its own action in here instead of putting
                    it inside the scroll area. Step7RFPPreview's send bar does. */}
                <div id="wizard-step-footer" className="flex items-center gap-3" />
                {currentStep !== STEPS.length ? (
                  <Button
                    onClick={nextStep}
                    disabled={!canProceed() || saving}
                  >
                    {saving ? "Saving..." : "Next"}
                    <ArrowRight className="h-4 w-4 ml-2" />
                  </Button>
                ) : !isRFPMode ? (
                  // Quote flow: finish actions live here for consistency with
                  // the other steps' footer.
                  <>
                    <Button
                      variant="outline"
                      onClick={onClose}
                      disabled={isFinalizing}
                    >
                      Save & Exit
                    </Button>
                    <Button
                      onClick={handleFinalizeClick}
                      disabled={isFinalizing}
                      className="gap-2"
                    >
                      {isFinalizing ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <FileText className="h-4 w-4" />
                      )}
                      Finalize Quote
                    </Button>
                  </>
                ) : null}
              </div>
            </div>
          </div>
        </div>
        {/* Rate Selection Dialog */}
        {renderRateSelectionDialog()}

        <AlertDialog open={finalizeConfirmOpen} onOpenChange={setFinalizeConfirmOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Finalize this quote?</AlertDialogTitle>
              <AlertDialogDescription>
                It will no longer be a draft, so you won't be able to reopen and edit it from
                the quotes list. Use <strong>Save &amp; Exit</strong> instead if you want to
                come back to it.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={handleFinalize}>
                <FileText className="h-4 w-4 mr-2" />
                Finalize Quote
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </QuoteWizardContext.Provider>
  );
}
